import { Controller } from "@hotwired/stimulus";

/**
 * Post Autosave Controller
 * 
 * Automatically saves the post form every minute without page refresh.
 * Shows a notice when autosave is successful.
 */
export default class extends Controller {
  static targets = ["form", "notice"]
  static values = {
    postId: String,
    autosaveInterval: { type: Number, default: 30000 } // 30 seconds
  }

  connect() {
    console.log("Post Autosave Controller connected", { postId: this.postIdValue, intervalMs: this.autosaveIntervalValue });
    
    if (!this.postIdValue) {
      console.warn("Post ID not available - autosave disabled");
      return;
    }

    const form = this.findForm();
    if (!form) {
      console.error("Form not found for autosave", {
        selectors: ['form.edit_post', 'form.new_post', 'form[action*="/admin/posts"]']
      });
      return;
    }

    this.formElement = form;

    this.autosaveInterval = null;
    this.isSaving = false;
    this.lastSavedAt = null;

    // Prevent form change events from triggering autosave during Action Cable updates
    // We'll rely on the interval timer only, not on form change events
    this.preventFormChangeTriggers = true;

    console.log("🔔 Starting autosave timer", { intervalMs: this.autosaveIntervalValue });
    // Start autosave timer
    this.startAutosave();

    // When user switches away (tab hidden / another app), save so we don't lose edits
    this.boundOnVisibilityChange = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
  }

  disconnect() {
    this.stopAutosave();
    if (this.boundOnVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
    }
  }

  onVisibilityChange() {
    if (document.visibilityState !== 'hidden' && !document.hidden) return;
    console.log("📤 Tab hidden — triggering autosave before switch away");
    this.performAutosave();
  }

  startAutosave() {
    // Save immediately on connect (after a short delay to let page load)
    setTimeout(() => {
      console.log("⏳ Initial autosave firing after delay");
      this.performAutosave();
    }, 2000); // Wait 2 seconds after page load

    // Then save on interval
    this.autosaveInterval = setInterval(() => {
      console.log("⏱️ Autosave interval tick");
      this.performAutosave();
    }, this.autosaveIntervalValue);
  }

  stopAutosave() {
    if (this.autosaveInterval) {
      clearInterval(this.autosaveInterval);
      this.autosaveInterval = null;
    }
  }

  async performAutosave() {
    if (this.isSaving) {
      console.log("Autosave already in progress, skipping...");
      return;
    }

    // Check if post is published - skip autosave for published posts
    const form = this.formElement || this.findForm();
    if (!form) {
      console.warn("Autosave aborted - form not found");
      return;
    }
    
    // Check if post is published - skip autosave for published posts
    const statusInput = form.querySelector('select[name*="[status]"], input[name*="[status]"]');
    if (statusInput) {
      const status = statusInput.value || statusInput.getAttribute('value');
      if (status === 'published') {
        console.log("⏸️ Autosave skipped - Post is published");
        return;
      }
    }

    // Check if lock takeover modal is visible - pause autosave while modal is shown
    const visibleModal = document.querySelector('.lock-takeover-modal-content');
    if (visibleModal && visibleModal.style.display !== 'none') {
      console.log("⏸️ Autosave paused - Lock takeover modal is visible");
      return;
    }
    
    // Also check for pending takeover requests container being visible
    const pendingRequestsContainer = document.getElementById('pending-takeover-requests-container');
    if (pendingRequestsContainer && pendingRequestsContainer.style.display !== 'none') {
      const hasRequests = document.querySelectorAll('[id^="lock-takeover-request-"]').length > 0;
      if (hasRequests) {
        console.log("⏸️ Autosave paused - Pending takeover requests are visible");
        return;
      }
    }

    const formData = new FormData(form);
    
    // Capture TipTap editor content from hidden input
    const contentInput = document.getElementById('post_content_input');
    if (contentInput && contentInput.value) {
      formData.set('post[content]', contentInput.value);
    }

    // Add autosave parameter to indicate this is an autosave request
    formData.append('autosave', 'true');

    this.isSaving = true;
    console.log("🔄 Starting autosave...", { timestamp: new Date().toISOString() });

    try {
      const response = await fetch(`/admin/posts/${this.postIdValue}`, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Autosave failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        this.lastSavedAt = new Date();
        this.showNotice("Changes auto-saved");
        console.log("✅ Autosave successful", { savedAt: this.lastSavedAt });
      } else {
        console.warn("Autosave returned success: false", data.errors);
      }
    } catch (error) {
      console.error("❌ Autosave error:", error);
      // Don't show error notice to user - keep it silent
    } finally {
      this.isSaving = false;
    }
  }

  hasFormChanges(formData) {
    // Simple check: if we have form data, assume there are changes
    // You could implement more sophisticated change detection here
    return formData.entries().next().done === false;
  }

  showNotice(message) {
    if (!this.hasNoticeTarget) {
      // Create notice element if it doesn't exist
      this.createNoticeElement();
    }

    const notice = this.noticeTarget;
    notice.textContent = message;
    notice.classList.remove("autosave-notice-hidden", "autosave-notice-fading");
    notice.classList.add("autosave-notice-visible");

    // Start fading out after showing for a moment, then hide completely
    setTimeout(() => {
      notice.classList.add("autosave-notice-fading");
      setTimeout(() => {
        this.hideNotice();
      }, 1000); // Wait for fade animation to complete (1 second)
    }, 9000); // Start fading after 9 seconds, total visible time ~10 seconds
  }

  hideNotice() {
    if (!this.hasNoticeTarget) return;

    const notice = this.noticeTarget;
    notice.classList.remove("autosave-notice-visible");
    notice.classList.add("autosave-notice-hidden");

    // Remove from DOM after animation completes
    setTimeout(() => {
      if (notice.parentNode) {
        notice.classList.remove("autosave-notice-hidden");
      }
    }, 300); // Match CSS transition duration
  }

  createNoticeElement() {
    const notice = document.createElement("div");
    notice.id = "autosave-notice";
    notice.setAttribute("data-post-autosave-target", "notice");
    notice.className = "autosave-notice autosave-notice-hidden";
    // Append to controller element so Stimulus recognizes it as a target
    this.element.appendChild(notice);
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }

  findForm() {
    if (this.formElement) return this.formElement;
    if (this.hasFormTarget) return this.formTarget;

    const selectors = [
      'form.edit_post',
      'form.new_post',
      'form[action*="/admin/posts"]'
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el) return el;
    }

    return null;
  }
}
